import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { plural, pluralizeIssues } from '../../../../../shared/helpers';
import type { VulnIgnoreReason } from '../../../../../shared/issuesV2';
import {
  Category,
  ExceptionIgnoreScope,
  ExceptionPackageScope,
  IgnoredKind,
  VulnIgnoreReasonDescriptions,
  VulnIgnoreReasons,
} from '../../../../../shared/issuesV2';
import { useToggle } from '../../../../hooks';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { SelectOption } from '../../../../types';
import { ignoreOrResolveWord, ignoreOrResolveWordActive, selectOptionFactory } from '../../../../utils';
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  ButtonGroup,
  ErrorAlert,
  FormGroup,
  InlineLoaderButton,
  LabeledTextArea,
  Select,
} from '../../../generic';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import InfoPanel from '../../../generic/InfoPanel';
import IgnoreReasonTitle from '../../../shared/vulns/IgnoreReasonTitle';
import useClearErrorsOnUnmount from '../hooks/useClearErrorsOnUnmount';
import IgnoreScopeOptions from './IgnoreScopeOptions';
import PackageScopeOptions from './PackageScopeOptions';

type IgnoreIssuesModalBaseProps = {
  show: boolean;
  selectedIssuesCount: number;
  onHide: () => void;
};

type IgnoreIssuesModalProps = IgnoreIssuesModalBaseProps & {
  onIgnore: (ignoreProps: {
    ignoredKind: IgnoredKind.IssueException;
    ignoreScope: ExceptionIgnoreScope;
    packageScope: ExceptionPackageScope;
    notes: string;
    resolutionReason?: VulnIgnoreReason;
  }) => void;
};

/**
 * IgnoreIssuesModal represents the UI for the modal for ignoring issues.
 */
const IgnoreWithExceptionsModal: VoidFunctionComponent<IgnoreIssuesModalProps> = ({
  show,
  selectedIssuesCount,
  onHide,
  onIgnore,
}) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);

  const status = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);
  const { isLoading } = status;

  const [notes, setNotes] = useState<string>('');
  const [ignoreScope, setIgnoreScope] = useState<ExceptionIgnoreScope>(
    scope.type === 'releaseGroup' ? ExceptionIgnoreScope.ReleaseGroup : ExceptionIgnoreScope.Project,
  );
  const [packageScope, setPackageScope] = useState<ExceptionPackageScope>(ExceptionPackageScope.SelectedVersion);
  const [willIncludeReleaseGroups, onToggleAndReleaseGroups] = useToggle(false);
  const [resolutionReason, setResolutionReason] = useState<VulnIgnoreReason>(VulnIgnoreReasons.ComponentNotPresent);

  const onIgnoreClick = () =>
    onIgnore({
      ignoredKind: IgnoredKind.IssueException,
      // Override the ignore scope if they've selected "and release groups"
      ignoreScope:
        ignoreScope === ExceptionIgnoreScope.Project && willIncludeReleaseGroups
          ? ExceptionIgnoreScope.ProjectAndReleaseGroups
          : ignoreScope,
      packageScope,
      notes,
      resolutionReason,
    });

  const onEnter = () => {
    setNotes('');
    setResolutionReason(VulnIgnoreReasons.ComponentNotPresent);
  };

  //  Ensure that when the component unmounts, we remove any error messages
  useClearErrorsOnUnmount();

  return (
    <Modal
      backdrop="static"
      animation
      show={show}
      onEnter={onEnter}
      onHide={onHide}
      onEscapeKeyDown={onHide}
      className="issues-modal"
    >
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">
          {`Ignore ${pluralizeIssues(selectedIssuesCount)}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <Flex direction="column" justify="flex-start" align="flex-start">
          <span className="issues-modal__body__message">
            {`${selectedIssuesCount.toLocaleString()} ${pluralizeIssues(selectedIssuesCount)} will be ignored.`}
          </span>
          <IgnoreScopeOptions
            setIgnoreScope={setIgnoreScope}
            ignoreScope={ignoreScope}
            willIncludeReleaseGroups={willIncludeReleaseGroups}
            issueCategory={issueCategory}
            scope={scope}
            onToggleAndReleaseGroups={onToggleAndReleaseGroups}
          />
          <PackageScopeOptions packageScope={packageScope} setPackageScope={setPackageScope} />
          {issueCategory === Category.Vulnerability && (
            <FormGroup legend={<IgnoreReasonTitle title="Reason for ignoring?" />} spacing="sm">
              <Select
                id="ignore-reason-dropdown"
                placeholder="Select an ignore reason (Optional)"
                value={{ value: resolutionReason, label: VulnIgnoreReasonDescriptions[resolutionReason] }}
                options={Object.entries(VulnIgnoreReasonDescriptions).map(([ignoreReason, label]) => {
                  return selectOptionFactory(ignoreReason, label);
                })}
                onChange={(o: SelectOption | null) => {
                  if (o) {
                    setResolutionReason(o.value as VulnIgnoreReason);
                  }
                }}
              />
            </FormGroup>
          )}
          <div className="issues-modal__body__notes">
            <LabeledTextArea
              label={`Add a note describing why ${plural(selectedIssuesCount, 'this is', 'these are')} ignored:`}
              onChange={setNotes}
              isDisabled={isLoading}
              value={notes}
              placeholder="Type your note..."
            />
          </div>
        </Flex>
        {status.errors && (
          <div className="issues-modal__body__errors">
            <ErrorAlert status={status} />
          </div>
        )}
        {/* Only show this if they're trying to ignore for all versions or for a scope outside of the immediate project/RG */}
        {(packageScope !== ExceptionPackageScope.SelectedVersion ||
          (ignoreScope !== ExceptionIgnoreScope.Project && ignoreScope !== ExceptionIgnoreScope.ReleaseGroup) ||
          willIncludeReleaseGroups) && (
          <InfoPanel>
            <div className="fossa-font-size-sm">
              This action will create the {ignoreOrResolveWord().toLowerCase()} rule based on your selections and will
              take a few moments to process.
            </div>
          </InfoPanel>
        )}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} isDisabled={isLoading} onClick={onHide}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={onIgnoreClick}
          >
            {isLoading ? `${ignoreOrResolveWordActive()} issues...` : 'Ignore'}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default IgnoreWithExceptionsModal;
